import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  ErrorMessage,
  Field,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import type { Author } from "@a7y/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ImageUploader } from "../components/ImageUploader";

interface AuthorDraft {
  name: string;
  avatarUrl: string;
  url: string;
}

const EMPTY_DRAFT: AuthorDraft = { name: "", avatarUrl: "", url: "" };

function draftFromAuthor(author: Author): AuthorDraft {
  return { name: author.name, avatarUrl: author.avatarUrl, url: author.url ?? "" };
}

/**
 * The author registry (A7Y-4474): backs `BlogPost.authorIds` and the public
 * By-line / About-the-Author read model (`content:authors`, D16). CRUD here
 * is the only way an author gains an avatar or a bio link — there is no
 * separate admin surface for it.
 */
export function AuthorsTab() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [dialogAuthor, setDialogAuthor] = useState<Author | "new" | null>(null);
  const [draft, setDraft] = useState<AuthorDraft>(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  const authorsQuery = useQuery({
    queryKey: ["blog-authors"],
    queryFn: () => api.blog.authors.list(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["blog-authors"] });

  const createMutation = useMutation({
    mutationFn: (data: AuthorDraft) =>
      api.blog.authors.create({
        name: data.name.trim(),
        avatarUrl: data.avatarUrl.trim(),
        ...(data.url.trim() && { url: data.url.trim() }),
      }),
    onSuccess: (res) => {
      if (res.ok) {
        invalidate();
        setDialogAuthor(null);
      } else {
        setFieldErrors(res.fieldErrors ?? {});
        setActionError(res.error);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AuthorDraft }) =>
      api.blog.authors.update(id, {
        name: data.name.trim(),
        avatarUrl: data.avatarUrl.trim(),
        url: data.url.trim() || undefined,
      }),
    onSuccess: (res) => {
      if (res.ok) {
        invalidate();
        setDialogAuthor(null);
      } else {
        setFieldErrors(res.fieldErrors ?? {});
        setActionError(res.error);
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.blog.authors.remove(id),
    onSuccess: (res) => {
      if (res.ok) invalidate();
      else setActionError(res.error);
    },
  });

  const openCreate = () => {
    setDraft(EMPTY_DRAFT);
    setFieldErrors({});
    setActionError(null);
    setDialogAuthor("new");
  };

  const openEdit = (author: Author) => {
    setDraft(draftFromAuthor(author));
    setFieldErrors({});
    setActionError(null);
    setDialogAuthor(author);
  };

  const handleSave = () => {
    if (dialogAuthor === "new") {
      createMutation.mutate(draft);
    } else if (dialogAuthor) {
      updateMutation.mutate({ id: dialogAuthor.id, data: draft });
    }
  };

  const handleRemove = async (author: Author) => {
    setActionError(null);
    const ok = await confirm({
      title: "Remove author?",
      description: `"${author.name}" will be removed from the author registry. This is refused while any post still lists them.`,
      confirmLabel: "Remove",
      confirmColor: "red",
    });
    if (ok) removeMutation.mutate(author.id);
  };

  const authors = authorsQuery.data?.authors ?? [];
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          The registry behind each post&rsquo;s byline and About-the-Author footer.
        </p>
        <Button color="blue" onClick={openCreate}>
          New author
        </Button>
      </div>

      {actionError && (
        <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          {actionError}
        </div>
      )}
      {authorsQuery.isError && (
        <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          {authorsQuery.error instanceof Error
            ? authorsQuery.error.message
            : "Failed to load authors"}
        </div>
      )}

      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader />
            <TableHeader>Name</TableHeader>
            <TableHeader>Link</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {authorsQuery.isLoading ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : authors.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="py-8 text-center text-zinc-500">
                No authors yet.
              </TableCell>
            </TableRow>
          ) : (
            authors.map((author) => (
              <TableRow key={author.id}>
                <TableCell>
                  <img
                    src={author.avatarUrl}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover"
                  />
                </TableCell>
                <TableCell className="font-medium">{author.name}</TableCell>
                <TableCell className="text-zinc-500 dark:text-zinc-400">
                  {author.url ?? "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button plain onClick={() => openEdit(author)}>
                      Edit
                    </Button>
                    <Button
                      plain
                      disabled={removeMutation.isPending}
                      onClick={() => handleRemove(author)}
                    >
                      Remove
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={dialogAuthor !== null} onClose={() => setDialogAuthor(null)} size="sm">
        <DialogTitle>{dialogAuthor === "new" ? "New author" : "Edit author"}</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <Field>
              <Label>Name</Label>
              <Input
                className="mt-1.5"
                value={draft.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft({ ...draft, name: e.target.value })
                }
                invalid={!!fieldErrors.name}
              />
              {fieldErrors.name && <ErrorMessage>{fieldErrors.name}</ErrorMessage>}
            </Field>
            <Field>
              <Label>Avatar</Label>
              <div className="mt-1.5">
                <ImageUploader
                  slug="authors"
                  value={draft.avatarUrl || undefined}
                  onSelect={(url) => setDraft({ ...draft, avatarUrl: url })}
                  onClear={() => setDraft({ ...draft, avatarUrl: "" })}
                />
              </div>
              {fieldErrors.avatarUrl && <ErrorMessage>{fieldErrors.avatarUrl}</ErrorMessage>}
            </Field>
            <Field>
              <Label>Link (optional)</Label>
              <Input
                className="mt-1.5"
                value={draft.url}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft({ ...draft, url: e.target.value })
                }
                placeholder="https://…"
                invalid={!!fieldErrors.url}
              />
              {fieldErrors.url && <ErrorMessage>{fieldErrors.url}</ErrorMessage>}
            </Field>
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setDialogAuthor(null)}>
            Cancel
          </Button>
          <Button
            color="blue"
            disabled={isSaving || !draft.name.trim() || !draft.avatarUrl.trim()}
            onClick={handleSave}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AuthorsTab;
